/**
 * Square terrain authoring model for COMBAT.app's tile brushes — the pure, testable
 * heart of the GM's authoring tools. {@link CombatStage} owns the pointer
 * wiring + dispatch; everything *decided* (which cells a stroke covers, which terrain
 * each writes, which `set_terrain`/`set_edge` command a target issues) lives here so it
 * is unit-tested without a canvas — the same split the token geometry
 * uses, and the one the hex `/map` surface makes in `lib/hexMap/mapEdit.ts` (cell paint)
 * + `wallEdit.ts` (edge walls).
 *
 * **Two authoring gestures, two tools.**
 *   - **Freehand tile strokes** — the tile/body brushes (**solid** · **cover** · **erase**)
 *     paint the **cell body only** (never an edge): a left-drag across the board, each cell
 *     entered painted once, committed as ONE atomic `batch`. See the "Freehand tile strokes"
 *     section ({@link strokeCells}, {@link strokeTarget}, {@link tileStrokeCommand}).
 *   - **Rubber-band wall runs** — a separate dedicated **wall tool** lays edge walls: press a
 *     grid **vertex** ({@link vertexAt}), drag, and a straight **dominant-axis run** of unit
 *     edge-walls ({@link wallRunEdges}) previews to the vertex under the cursor; release commits
 *     the whole run as ONE atomic `batch` of `set_edge` children ({@link wallRunCommand}). See
 *     the "Rubber-band wall runs" section.
 *
 * **What a brush writes (`decisions/0185`).** The **tile** brush writes a catalogue tile reference
 * (`{ tile: id }`), picked from the TERRAIN window's palette; painting open ground clears the cell.
 * The **mechanic** brushes (solid · pit · cover · difficult · dangerous, under the window's
 * "Advanced" disclosure) write one-off custom cells (`{ tile: "custom", properties }`).
 *
 * **Toggle semantics, direction-at-press.** A brush over a press cell that *already* holds exactly
 * the cell that brush writes clears it (one brush places and removes): the same tile id for the tile
 * brush, a custom cell with the same properties for a mechanic brush ({@link sameStoredCell}). A
 * catalogue tile and a custom cell never match, even with identical mechanics, so a mechanic brush
 * over a "boulders" cell replaces it with custom hard cover rather than clearing it. The
 * eraser always clears. The wall tool is itself a single toggling tool: its run **clears** when
 * the first edge under the press vertex already carries a wall, else it **sets** — so the wall
 * tool needs no separate erase mode (the tile brushes mirror this with a dedicated eraser only
 * because they carry three distinct fills; a wall is one barrier, so toggle subsumes erase). A
 * no-op (re-painting an identical cell / re-walling an identical edge / erasing open ground)
 * issues nothing. Either way the place-vs-clear direction is decided ONCE from the press
 * cell/edge ({@link strokeTarget} / {@link wallRunCommand}) so the whole gesture is coherent.
 *
 * **Uncommitted-preview probes.** The same pure decisions also
 * drive COMBAT.app's live affordances — a hover hint on the target cell, a ghost of a drag in
 * progress, and an add-vs-remove read for both tools — so what the GM sees before release is what
 * will commit. {@link strokeDirection} maps a resolved {@link strokeTarget} to add (paint a fill)
 * vs remove (clear); {@link wallRunDirection} reads the same first-edge-under-the-press toggle
 * {@link wallRunCommand} commits. Both return the shared {@link AuthoringDirection} the stage
 * styles by. No new commit logic — the previews call these and {@link strokeCells} directly.
 */
import type {
  DamageType,
  EdgeMap,
  EdgeTerrain,
  EncounterBatchCommand,
  EncounterCommand,
  Room,
  RoomId,
  RoomRect,
  SquareCell,
  TerrainCell,
  TerrainMap,
  TerrainProperties,
  TileTypeId,
} from "@lancer-vtt/domain";
import {
  CUSTOM_TILE,
  DEFAULT_HAZARD_TYPE,
  OPEN_GROUND_TILE,
  customTerrainCell,
  normalizeTerrainCell,
  propertiesAtKey,
  roomContainsCell,
  squareCell,
  subtractRoomCells,
} from "@lancer-vtt/domain";
import { edgeKey, squareKey, squareSpace } from "@lancer-vtt/space";
import { CELL } from "./combatCells";

/**
 * The tile/body brushes the GM picks from — all paint the **whole cell** (no edge
 * sub-targeting): a **tile** from the catalogue (the palette's brush; which tile rides
 * {@link PaintOptions}' `tile`), or one of the Advanced mechanic brushes, which write custom
 * cells — a **solid** block (blocks movement + sight, flyable — a flyable boulder is the RAW-default outdoor obstacle), a **pit** (blocks ground movement only,
 * sight passes, a Flying mover crosses it but cannot
 * end there; the trench/chasm class), a **cover** patch (soft or hard, per {@link
 * CoverGrade}), **difficult** terrain (double move cost), **dangerous** terrain
 * (an ENGINEERING check or 5 damage of the chosen {@link DamageType}
 * on first entry/end-of-turn; orthogonal to move cost — a cell can be both dangerous and
 * difficult), or an **eraser**. Edge walls are a separate tool, not a brush in this set. The
 * TERRAIN window arms `tile` from its palette and the mechanic brushes from its Advanced
 * disclosure (and keys 2–5 / 9); `erase` is NOT a button — erasing rides the universal **Alt**-held modifier or the sticky erase toggle
 * (X) — but the brush stays in the model as the clear-target every gesture resolves to.
 */
export type CombatBrush = "tile" | "solid" | "pit" | "cover" | "difficult" | "dangerous" | "erase";

/** The mechanic brushes — the Advanced disclosure's set, which write custom cells. */
export type MechanicBrush = Exclude<CombatBrush, "tile" | "erase">;

/** Whether `brush` is one of the Advanced mechanic brushes (not the tile brush, not the eraser). */
export function isMechanicBrush(brush: CombatBrush | null): brush is MechanicBrush {
  return brush !== null && brush !== "tile" && brush !== "erase";
}

/** The tile the tile brush paints until the GM picks another. */
export const DEFAULT_PAINT_TILE: TileTypeId = "thick-brush";

/**
 * The cover degree the **cover** brush paints. The model carries `cover: "soft" | "hard"`
 * ({@link TerrainProperties}) and the rail's soft/hard toggle makes both authorable. Defaults to `hard`.
 */
export type CoverGrade = "soft" | "hard";

/** The move cost difficult terrain writes — double the ordinary 1 (Lancer: difficult terrain costs 2). */
export const DIFFICULT_MOVE_COST = 2;

/**
 * The hazard damage types the **dangerous** brush's rail sub-toggle offers — **the four the book
 * names, read off the book.** Core p.62: a character caught in dangerous terrain *"take[s] 5 damage
 * – kinetic, energy, explosive, or burn, depending on the hazard"*. Kinetic is first (the default
 * {@link DEFAULT_HAZARD_TYPE}).
 *
 * ## Why `heat` is absent, and why the reason is worth a paragraph
 *
 * This list used to carry a fifth member, `heat`, and it got there by being derived **by subtraction
 * from the {@link DamageType} union** — its own doc said "every `DamageType` except `variable`". The
 * union is a weapon-damage vocabulary, not a terrain one, so the subtraction inherited a type the
 * book rules out in as many words: Core p.67, *"Heat is a special type of harm that doesn't count as
 * damage."* Offering it taught the table that heat-dealing dangerous terrain is a thing Lancer
 * supports.
 *
 * **A list derived from a type instead of from the source of truth is the same failure as copy
 * derived from the surrounding code instead of from the book** — the defect family
 * `frontend/src/rulesCopy.pin.test.ts` exists for, at a seventh site. Do not re-add `heat` because
 * the union has it. `decisions/0170` carries the ruling and the developer's call.
 *
 * `variable` is excluded for a different, non-rules reason: it is a weapon-fire-time placeholder
 * that makes no sense painted onto static terrain.
 *
 * ## This is the AUTHORING palette, not the read path
 *
 * `combatCells.ts`'s `cellChannels` still types its hazard channel as the full {@link DamageType},
 * deliberately: a board persisted before this change can carry `{dangerous: true, hazard: "heat"}`,
 * the load-time encounter migrator carries `terrain` verbatim and folds no cell, and such a
 * cell must still load, draw and resolve rather than silently lose its hazard. The terrain painter
 * draws it with its type's rim colour (`terrainArt/tileArt.ts`'s `HAZARD_RIM`) — not this list's concern.
 */
export const HAZARD_TYPES: DamageType[] = ["kinetic", "energy", "explosive", "burn"];

/** The ordered brush palette (model order; the rail builds its own N-tool layout). Erase stays last. */
export const BRUSHES: CombatBrush[] = ["tile", "solid", "pit", "cover", "difficult", "dangerous", "erase"];

/**
 * The direction an authoring gesture resolves to, for the uncommitted preview's add-vs-remove
 * read: **add** = the gesture lays terrain / a wall (preview the
 * target fill / a gold line); **remove** = it clears back to open ground (preview a removal read).
 * Both the tile ghost ({@link strokeDirection}) and the wall preview ({@link wallRunDirection})
 * resolve to this off the SAME toggle the commit uses, so what the GM sees is what will commit.
 */
export type AuthoringDirection = "add" | "remove";

/** A human label for a brush (tool-rail button text). */
export function brushLabel(brush: CombatBrush): string {
  switch (brush) {
    case "tile":
      return "Tile";
    case "solid":
      return "Solid";
    case "pit":
      return "Pit";
    case "cover":
      return "Cover";
    case "difficult":
      return "Difficult";
    case "dangerous":
      return "Dangerous";
    case "erase":
      return "Erase";
  }
}

/**
 * The {@link TerrainProperties} a brush paints on a tile. Solid = blocks movement + sight, flyable;
 * pit = blocks movement ONLY — sight passes, and a Flying mover crosses it (an absent
 * `blocksFlight` is what gives it the pass-over-but-not-end-there read); cover =
 * the chosen {@link CoverGrade} (soft/hard, default hard); difficult = double move cost (no
 * sight/move block — rough ground you can still cross, just slower); dangerous = the chosen hazard
 * {@link DamageType} (default kinetic), no move/sight change (orthogonal to difficult).
 */
function tileFlagsFor(
  brush: MechanicBrush,
  coverGrade: CoverGrade = "hard",
  hazard: DamageType = DEFAULT_HAZARD_TYPE,
): TerrainProperties {
  switch (brush) {
    case "solid":
      return { blocksMovement: true, blocksSight: true };
    case "pit":
      return { blocksMovement: true };
    case "cover":
      return { cover: coverGrade };
    case "difficult":
      return { moveCost: DIFFICULT_MOVE_COST };
    case "dangerous":
      return { dangerous: true, hazard };
  }
}

/**
 * The wall tool's two authorable heights: **full** — a
 * floor-to-ceiling barrier, blocking movement + sight + flight (indoor walls are
 * floor-to-ceiling; this is also what auto-wall's room perimeter writes); **low** — a flyable
 * barrier that still blocks ground movement (sight passes; grants the picked cover grade — see
 * `edgeFlagsFor`'s `coverGrade` param). Defaults to `"full"` everywhere it's optional.
 */
export type WallHeight = "full" | "low";

/**
 * The {@link EdgeTerrain} the wall brush paints on an edge, by {@link WallHeight} (default full) and,
 * for a low wall, its cover grade. `coverGrade` reuses
 * {@link CoverGrade}'s **type** but NOT its state: the Cover cell brush's `coverGrade` state defaults
 * `"hard"` and is shared with the Cover brush, while the low wall's grade is tracked as its OWN state
 * defaulting `"soft"` (RAW p.66 names low walls as soft cover) — reusing the Cover brush's state would
 * force one of the two defaults to be wrong. A full-height wall carries no cover — it blocks LoS
 * outright, so cover (which only matters when sight passes) is the low wall's whole point;
 * `coverGrade` is ignored when `height` is `"full"`.
 */
function edgeFlagsFor(height: WallHeight = "full", coverGrade: CoverGrade = "soft"): EdgeTerrain {
  return height === "full"
    ? { blocksMovement: true, blocksSight: true, blocksFlight: true }
    : { blocksMovement: true, cover: coverGrade };
}

/** Whether two property sets are equal once sparsified (absent === default) — the custom-cell half of {@link sameStoredCell}. */
function sameCellFlags(a: TerrainProperties | undefined, b: TerrainProperties): boolean {
  const x = a ?? {};
  return (
    (x.blocksMovement ?? false) === (b.blocksMovement ?? false) &&
    (x.moveCost ?? 1) === (b.moveCost ?? 1) &&
    (x.blocksSight ?? false) === (b.blocksSight ?? false) &&
    (x.cover ?? "none") === (b.cover ?? "none") &&
    (x.dangerous ?? false) === (b.dangerous ?? false) &&
    // Hazard only meaningfully differs while `dangerous` is set on both sides — comparing it
    // unconditionally is still correct (both read the same default when `dangerous` is absent).
    (x.hazard ?? DEFAULT_HAZARD_TYPE) === (b.hazard ?? DEFAULT_HAZARD_TYPE)
  );
}

/**
 * The resolved properties of the cell at `cell` (through its tile, `decisions/0185`), or
 * `undefined` for open ground — for readers that need mechanics, never for the brush toggle.
 */
export function cellPropertiesAt(terrain: TerrainMap, cell: SquareCell): TerrainProperties | undefined {
  return propertiesAtKey(terrain, squareKey(cell));
}

/**
 * The stored cell at `cell` (its tile reference), or `undefined` for open ground — what the brushes
 * compare a target against. Read for the toggle only; mechanics go through {@link cellPropertiesAt}.
 */
export function storedCellAt(terrain: TerrainMap, cell: SquareCell): TerrainCell | undefined {
  return terrain[squareKey(cell)];
}

/** The cell every clear writes: open ground, which removes the key. */
export const OPEN_GROUND_CELL: TerrainCell = { tile: OPEN_GROUND_TILE };

/**
 * Whether two stored cells are the **same cell** once normalized, so a re-paint is a no-op and a
 * press on it toggles the brush off. Open ground, an absent key and an empty custom cell are all the
 * same (nothing). Catalogue tiles compare by **id**, not by mechanics — "trees" and "boulders" carry
 * the same hard cover and are still different tiles. Custom cells compare by their properties. A
 * catalogue tile never equals a custom cell.
 */
export function sameStoredCell(a: TerrainCell | undefined, b: TerrainCell | undefined): boolean {
  const x = a === undefined ? undefined : normalizeTerrainCell(a);
  const y = b === undefined ? undefined : normalizeTerrainCell(b);
  if (x === undefined || y === undefined) return x === y;
  if (x.tile === CUSTOM_TILE && y.tile === CUSTOM_TILE) return sameCellFlags(x.properties, y.properties);
  return x.tile === y.tile;
}

/** Whether two edge-flag sets are equal once sparsified (absent === default) — includes
 *  `blocksFlight`, so a full-vs-low wall never misreads as a no-op
 *  against the other height, AND `cover`, so a grade change on an otherwise-identical
 *  low wall never misreads as "already in target state" and silently no-ops. */
function sameEdgeFlags(a: EdgeTerrain | undefined, b: EdgeTerrain): boolean {
  const x = a ?? {};
  return (
    (x.blocksMovement ?? false) === (b.blocksMovement ?? false) &&
    (x.blocksSight ?? false) === (b.blocksSight ?? false) &&
    (x.blocksFlight ?? false) === (b.blocksFlight ?? false) &&
    (x.cover ?? "none") === (b.cover ?? "none")
  );
}

/** Whether an edge currently carries no wall (nothing to clear). */
function edgeIsOpen(flags: EdgeTerrain | undefined): boolean {
  return sameEdgeFlags(flags, {});
}

// ── Freehand tile strokes ─────────────────────────────────────────────────────────
//
// A tile brush (solid/cover/erase) paints a *freehand stroke*: a left-drag across the
// board, each cell entered painted once. Three pure pieces, tested without a canvas,
// drive it; the stage only samples the pointer and dispatches the result:
//   - `strokeCells`      — pointer samples → the ordered, DEDUPED cells the stroke covers
//                          (a cell touched once is never revisited, so it never re-toggles).
//   - `strokeTarget`     — the terrain EVERY cell writes, decided ONCE from the press cell
//                          (toggle direction fixed at press → no mid-drag flicker).
//   - `tileStrokeCommand`— the finished stroke as ONE atomic `batch` of `set_terrain`
//                          children (one undo entry), in-bounds cells only, no-ops dropped.
// Edge/wall authoring is NOT here — it is a separate tool; these brushes write
// the cell body only (the EDGE_BAND tile-vs-edge heuristic above is retired for them).

/** Floor a world-space point (px at scale 1) to the square cell containing it. */
export function cellAt(world: { x: number; y: number }): SquareCell {
  return squareCell(Math.floor(world.x / CELL), Math.floor(world.y / CELL));
}

/**
 * The ordered, de-duplicated cells a freehand stroke covers, from its pointer `samples`
 * (world px at scale 1, **press first**). Each sample floors to a cell; a cell already seen
 * this stroke is skipped, so re-crossing a cell never re-toggles it. `samples[0]` (the press)
 * is always `cells[0]`, which is what fixes the stroke's paint-vs-clear direction (see
 * {@link strokeTarget}). A single sample (a click with no drag) yields a one-cell stroke.
 */
export function strokeCells(samples: { x: number; y: number }[]): SquareCell[] {
  const seen = new Set<string>();
  const cells: SquareCell[] = [];
  for (const s of samples) {
    const cell = cellAt(s);
    const k = squareKey(cell);
    if (seen.has(k)) continue;
    seen.add(k);
    cells.push(cell);
  }
  return cells;
}

/**
 * The brush-specific paint options {@link strokeTarget}/{@link tileFlagsFor} read — bundled into
 * one object (rather than two trailing positional params) to stay under the repo's `max-params-3`
 * lint gate now that the dangerous brush adds a second per-brush option alongside `coverGrade`.
 */
export type PaintOptions = {
  /** The catalogue tile for the `tile` brush (default {@link DEFAULT_PAINT_TILE}); ignored by other brushes. */
  tile?: TileTypeId;
  /** The cover grade for the `cover` brush (soft/hard, default hard); ignored by other brushes. */
  coverGrade?: CoverGrade;
  /** The hazard type for the `dangerous` brush (default kinetic); ignored by other brushes. */
  hazard?: DamageType;
};

/**
 * The stored cell a brush writes before the toggle is applied: the picked catalogue tile for the
 * `tile` brush (open ground clears), a custom cell for a mechanic brush, open ground for the eraser.
 */
export function brushCell(brush: CombatBrush, opts: PaintOptions = {}): TerrainCell {
  if (brush === "erase") return OPEN_GROUND_CELL;
  if (brush === "tile") return { tile: opts.tile ?? DEFAULT_PAINT_TILE };
  return customTerrainCell(tileFlagsFor(brush, opts.coverGrade ?? "hard", opts.hazard ?? DEFAULT_HAZARD_TYPE));
}

/**
 * The cell EVERY cell in a stroke writes, decided once from the stored cell under the initial
 * press. A brush **toggles** on the press cell: if it already holds exactly the cell the brush
 * writes ({@link sameStoredCell} — the same tile id, or the same custom properties) the whole stroke
 * CLEARS (open ground); otherwise the whole stroke PAINTS it. The eraser always clears. Deciding at
 * press keeps the stroke coherent — one intent, no mid-drag flicker between paint and clear.
 */
export function strokeTarget(brush: CombatBrush, pressCell: TerrainCell | undefined, opts: PaintOptions = {}): TerrainCell {
  const want = brushCell(brush, opts);
  return sameStoredCell(pressCell, want) ? OPEN_GROUND_CELL : want;
}

/**
 * Whether a resolved stroke {@link strokeTarget} reads as an **add** (painting a cell) or a
 * **remove** (clearing back to open ground) — the cue the live tile ghost / hover hint styles
 * itself by. A target that stores as nothing (open ground, an empty custom cell) is a remove;
 * anything else is an add. Pure, so the preview's add-vs-remove read is the SAME decision the
 * commit makes — it can't drift from what `tileStrokeCommand` writes.
 */
export function strokeDirection(target: TerrainCell): AuthoringDirection {
  return normalizeTerrainCell(target) === undefined ? "remove" : "add";
}

/** Named arguments for {@link tileStrokeCommand}. */
export type TileStrokeArgs = {
  actorId: string;
  brush: CombatBrush;
  /** Pointer samples over the drag (world px at scale 1), **press first**. A lone sample = a click. */
  samples: { x: number; y: number }[];
  /** The catalogue tile for the `tile` brush; ignored by other brushes. */
  tile?: TileTypeId;
  /** The cover grade for the `cover` brush (soft/hard, default hard); ignored by other brushes. */
  coverGrade?: CoverGrade;
  /** The hazard type for the `dangerous` brush (default kinetic); ignored by other brushes. */
  hazard?: DamageType;
  /** The live terrain map — read for the press-cell direction and to drop no-op children. */
  terrain: TerrainMap;
  /** Board bounds — cells outside `[0,cols)×[0,rows)` are dropped (no off-board tiles). */
  cols: number;
  rows: number;
};

/**
 * The ONE atomic `batch` of `set_terrain` children a finished tile stroke commits — or `null`
 * when nothing needs to change. The place-vs-clear direction is fixed by the press cell
 * ({@link strokeTarget}); every in-bounds stroke cell whose current flags differ from that
 * target contributes one child; cells already in the target state, and off-board cells, are
 * dropped. ONE batch ⇒ ONE undo entry — the generic bus inverts it atomically (never dispatch
 * N leaf `set_terrain`, or undo unwinds the stroke one cell at a time).
 */
export function tileStrokeCommand(args: TileStrokeArgs): EncounterBatchCommand | null {
  const { actorId, brush, samples, tile, coverGrade, hazard, terrain, cols, rows } = args;
  const cells = strokeCells(samples);
  if (cells.length === 0) return null;
  const target = strokeTarget(brush, storedCellAt(terrain, cells[0]!), { tile, coverGrade, hazard });
  const commands: EncounterCommand[] = [];
  for (const cell of cells) {
    if (cell.x < 0 || cell.x >= cols || cell.y < 0 || cell.y >= rows) continue;
    if (sameStoredCell(storedCellAt(terrain, cell), target)) continue; // no-op: already in target state
    commands.push({ type: "set_terrain", actorId, cell, terrain: target });
  }
  if (commands.length === 0) return null;
  return { type: "batch", commands };
}

// ── Rubber-band box fill ──────────────────────────────────────────────────────────
//
// The tile brushes' PRIMARY gesture is a rubber-band BOX, not a freehand stroke: press a
// corner, drag, release → the whole clamped rectangle fills with the active brush (a click
// with no drag is a 1×1 box). Unlike the freehand stroke this can never skip a cell on a fast
// drag — the box is defined by its two corners, not a sampled path. Two pure pieces drive it
// (the stage floors the press/release to cells and dispatches the result):
//   - `boxCells`        — two corner cells → the clamped, row-major-ordered list of cells the
//                         box covers (the same clamp `roomRectFromCells` uses, so the tile box
//                         and the room box agree on bounds).
//   - `tileBoxCommand`  — the finished box as ONE atomic `batch` of `set_terrain` children, the
//                         place-vs-clear direction fixed by the press cell (or forced to clear by
//                         the Alt-held erase modifier), no-op + off-board cells dropped.
// The freehand stroke above stays as a Shift-held modifier (organic shapes a box can't make);
// the box is the default. Direction-at-press + one-batch-one-undo are unchanged from the stroke.

/**
 * The clamped, row-major-ordered cells a rubber-band box between two corner cells covers — the
 * cell-list analogue of {@link roomRectFromCells} (it reuses that same board clamp, so the tile
 * box and the room box never disagree on bounds). Both corners are *inclusive* (a press and
 * release on the same cell is a single cell); the box is intersected with the board
 * `[0,cols)×[0,rows)`, so a partly-off-board drag yields only its on-board cells and a wholly
 * off-board drag yields `[]`. Cells are emitted top-to-bottom, left-to-right (row-major).
 */
export function boxCells(start: SquareCell, end: SquareCell, board: { cols: number; rows: number }): SquareCell[] {
  const rect = roomRectFromCells(start, end, { cols: board.cols, rows: board.rows });
  if (!rect) return [];
  const cells: SquareCell[] = [];
  for (let y = rect.y; y < rect.y + rect.h; y++) {
    for (let x = rect.x; x < rect.x + rect.w; x++) {
      cells.push(squareCell(x, y));
    }
  }
  return cells;
}

/** Named arguments for {@link tileBoxCommand}. */
export type TileBoxArgs = {
  actorId: string;
  brush: CombatBrush;
  /** The drag-start (press) cell — its flags fix the box's paint-vs-clear direction. */
  start: SquareCell;
  /** The drag-release cell — with `start`, bounds the filled box. */
  end: SquareCell;
  /**
   * The Alt-held **erase** modifier: when true the
   * whole box clears to open ground regardless of the press cell's state (the same result as the
   * dedicated Erase brush, applied as a drag modifier). When false/absent the press-cell toggle
   * decides direction, exactly like {@link strokeTarget}.
   */
  erase?: boolean;
  /** The catalogue tile for the `tile` brush; ignored by other brushes. */
  tile?: TileTypeId;
  /** The cover grade for the `cover` brush (soft/hard, default hard); ignored by other brushes. */
  coverGrade?: CoverGrade;
  /** The hazard type for the `dangerous` brush (default kinetic); ignored by other brushes. */
  hazard?: DamageType;
  /** The live terrain map — read for the press-cell direction and to drop no-op children. */
  terrain: TerrainMap;
  /** Board bounds — cells outside `[0,cols)×[0,rows)` are dropped (no off-board tiles). */
  cols: number;
  rows: number;
};

/**
 * The ONE atomic `batch` of `set_terrain` children a finished rubber-band tile box commits — or
 * `null` when nothing needs to change. The place-vs-clear direction is fixed ONCE: by the press
 * cell ({@link strokeTarget}), or forced to **clear** by the Alt-held `erase` modifier. Every
 * in-bounds box cell whose current flags differ from that target contributes one child; cells
 * already in the target state are dropped (`boxCells` has already dropped the off-board ones).
 * ONE batch ⇒ ONE undo entry — the same atomic-gesture grain the freehand stroke and wall run
 * use, so a box paints/clears and undoes in a single step.
 */
export function tileBoxCommand(args: TileBoxArgs): EncounterBatchCommand | null {
  const { actorId, brush, start, end, erase, tile, coverGrade, hazard, terrain, cols, rows } = args;
  const cells = boxCells(start, end, { cols, rows });
  if (cells.length === 0) return null;
  const target = erase ? OPEN_GROUND_CELL : strokeTarget(brush, storedCellAt(terrain, start), { tile, coverGrade, hazard });
  const commands: EncounterCommand[] = [];
  for (const cell of cells) {
    if (sameStoredCell(storedCellAt(terrain, cell), target)) continue; // no-op: already in target state
    commands.push({ type: "set_terrain", actorId, cell, terrain: target });
  }
  if (commands.length === 0) return null;
  return { type: "batch", commands };
}

// ── Rubber-band wall runs ─────────────────────────────────────────────────────────
//
// The dedicated **wall tool** lays edge walls by a rubber-band gesture: press a grid
// vertex, drag, release. A straight **dominant-axis** run of unit edge-walls is laid from
// the press vertex to the vertex under the cursor, committed as ONE atomic `batch` of
// `set_edge` children. Like the tile stroke, three pure pieces drive it (the stage only
// snaps the pointer, previews, and dispatches):
//   - `vertexAt`        — a world point → the nearest grid vertex (integer cell corner).
//   - `wallRunEdges`    — two vertices → the ordered list of unit edges along the dominant
//                         axis (each as the `(a,b)` cell pair straddling that grid line).
//   - `wallRunCommand`  — the finished run as ONE `batch` of `set_edge` children, direction
//                         (set vs clear) fixed by the first edge under the press vertex,
//                         no-ops dropped, fully-off-board edges dropped (a border wall — one
//                         side off-board — is kept; the reducer is the real wall).
// `wallRunEndpoint` gives the projected dominant-axis endpoint for the live preview line.

/** A grid vertex — an integer lattice corner where up to four cells meet. */
export type Vertex = { x: number; y: number };

/** Snap a world-space point (px at scale 1) to the nearest grid **vertex** (`round(world / CELL)`). */
export function vertexAt(world: { x: number; y: number }): Vertex {
  return { x: Math.round(world.x / CELL), y: Math.round(world.y / CELL) };
}

/**
 * The dominant-axis projected endpoint of a rubber-band run from vertex `a` toward vertex
 * `b` — the vertex the straight run actually reaches. The run snaps to whichever axis the
 * drag covered furthest: **horizontal** when `|dx| >= |dy|` (ties → horizontal), else
 * **vertical**. (A diagonal drag thus resolves to its longer axis; an L-path is a possible
 * later enhancement, deferred for v1.) Used to draw the live preview line.
 */
export function wallRunEndpoint(a: Vertex, b: Vertex): Vertex {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.abs(dx) >= Math.abs(dy) ? { x: b.x, y: a.y } : { x: a.x, y: b.y };
}

/**
 * The ordered list of unit wall edges along the dominant-axis straight run from vertex `a`
 * to vertex `b`, each as the `(a, b)` **cell** pair straddling that grid line. The run is a
 * single axis-aligned line (see {@link wallRunEndpoint} for the axis choice). For a
 * **horizontal** run along grid line `y = a.y`, the unit edge on column `x` straddles the
 * cell above (`x, y-1`) and the cell below (`x, y`); for a **vertical** run along `x = a.x`,
 * the unit edge on row `y` straddles the cell left (`x-1, y`) and the cell right (`x, y`).
 * The list **starts at the edge adjacent to `a`**, so `edges[0]` fixes the run's toggle
 * direction (the analogue of the tile stroke's press cell). A zero-length run (no drag /
 * same vertex) yields `[]`. Off-board cells are not filtered here — that is
 * {@link wallRunCommand}'s job (a border wall has one off-board side and is legitimate).
 */
export function wallRunEdges(a: Vertex, b: Vertex): { a: SquareCell; b: SquareCell }[] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const edges: { a: SquareCell; b: SquareCell }[] = [];
  if (Math.abs(dx) >= Math.abs(dy)) {
    // Horizontal run on grid line y = a.y. The edge between consecutive vertices sits on a
    // cell column: rightward that column is `a.x + i`, leftward it is `a.x - 1 - i`.
    const row = a.y;
    const step = dx >= 0 ? 1 : -1;
    for (let i = 0; i < Math.abs(dx); i++) {
      const x = a.x + i * step + (step < 0 ? -1 : 0);
      edges.push({ a: squareCell(x, row - 1), b: squareCell(x, row) });
    }
  } else {
    // Vertical run on grid line x = a.x. The edge between consecutive vertices sits on a cell
    // row: downward that row is `a.y + i`, upward it is `a.y - 1 - i`.
    const col = a.x;
    const step = dy >= 0 ? 1 : -1;
    for (let i = 0; i < Math.abs(dy); i++) {
      const y = a.y + i * step + (step < 0 ? -1 : 0);
      edges.push({ a: squareCell(col - 1, y), b: squareCell(col, y) });
    }
  }
  return edges;
}

/**
 * Whether a rubber-band wall run from vertex `from` toward `to` would **set** walls at `height`
 * (and, for a low wall, `coverGrade`) (`"add"`) or **clear** the existing wall (`"remove"`), or
 * `null` for a zero-length run (no edge under the press yet). The direction is read off the
 * **first edge under the press vertex** ({@link wallRunEdges}'s `edges[0]`) — matching the armed
 * `height`'s (+ `coverGrade`'s) target flags ⇒ clear (a second press of the SAME height AND grade
 * toggles it off, mirroring the tile stroke); anything else (open ground, a wall of the *other*
 * height, OR a low wall of the *other* grade) ⇒ set/replace with the armed target — i.e. EXACTLY
 * the toggle {@link wallRunCommand} commits, so the live preview's set-vs-clear cue matches the
 * commit. Height and cover grade are bundled into this same options object rather than new
 * parameters, to stay under the repo's max-params-3 lint gate.
 */
export function wallRunDirection(
  from: Vertex,
  to: Vertex,
  opts: { edges: EdgeMap; height?: WallHeight; coverGrade?: CoverGrade },
): AuthoringDirection | null {
  const first = wallRunEdges(from, to)[0];
  if (!first) return null;
  return sameEdgeFlags(opts.edges[edgeKey(squareSpace, first.a, first.b)], edgeFlagsFor(opts.height, opts.coverGrade))
    ? "remove"
    : "add";
}

/** Named arguments for {@link wallRunCommand}. */
export type WallRunArgs = {
  actorId: string;
  /** The pressed grid vertex (the run's start). */
  from: Vertex;
  /** The grid vertex under the cursor at release (the run's end). */
  to: Vertex;
  /** The live edge map — read for the press-edge toggle direction and to drop no-op children. */
  edges: EdgeMap;
  /**
   * The Alt-held **erase** modifier: when true the
   * whole run CLEARS regardless of the press edge's state (the same Alt = erase modifier the tile
   * box uses, so the trigger is consistent across tools). When false/absent the press-edge toggle
   * decides set-vs-clear, exactly as before.
   */
  erase?: boolean;
  /**
   * The wall height to author: `"full"` (default) — blocks
   * movement + sight + flight; `"low"` — blocks ground movement only, flyable, sight passes, grants
   * `coverGrade`.
   */
  height?: WallHeight;
  /**
   * The cover grade a **low** wall grants: `"soft"`
   * (default) or `"hard"`. Ignored when `height` is `"full"` (a floor-to-ceiling wall blocks LoS
   * outright — cover is the low wall's whole point). A SEPARATE value from the Cover cell brush's
   * `coverGrade` (see `edgeFlagsFor`'s doc comment) — bundled here alongside `height` rather than a
   * new parameter, to stay under the repo's max-params-3 lint gate.
   */
  coverGrade?: CoverGrade;
  /** Board bounds — an edge is kept if at least one of its two cells is on-board. */
  cols: number;
  rows: number;
};

/**
 * The ONE atomic `batch` of `set_edge` children a finished wall run commits — or `null` when
 * nothing needs to change. The set-vs-clear direction is fixed ONCE by the **first edge under
 * the press vertex** ({@link wallRunEdges}'s `edges[0]`): if it already carries a wall matching the
 * armed `height` the whole run CLEARS (`{}`); otherwise the whole run SETS/REPLACES with `height`'s
 * barrier (open ground *or* the other height both resolve to "set", so pressing **low** over an
 * existing **full** wall downgrades it in one stroke rather than requiring a clear-then-repaint) —
 * so the wall tool both places and removes with no separate erase mode (mirrors the tile stroke's
 * direction-at-press). Every run edge whose current flags differ from that target contributes one
 * child; no-op edges (already in the target state) are dropped, and a fully-off-board edge (both
 * cells outside the board) is dropped — but a **border** edge (one side off-board) is kept, since an
 * outer-border wall is legitimate (the reducer, which checks only adjacency, is the real wall). ONE
 * batch ⇒ ONE undo entry — the generic bus inverts it atomically (never dispatch N leaf `set_edge`).
 */
export function wallRunCommand(args: WallRunArgs): EncounterBatchCommand | null {
  const { actorId, from, to, edges, erase, height, coverGrade, cols, rows } = args;
  const runEdges = wallRunEdges(from, to);
  if (runEdges.length === 0) return null; // zero-length run (no drag): nothing to author
  // Alt-held erase forces a clear; otherwise the first-edge-under-the-press toggle decides.
  const dir = erase ? "remove" : wallRunDirection(from, to, { edges, height, coverGrade });
  if (dir === null) return null;
  const target: EdgeTerrain = dir === "add" ? edgeFlagsFor(height, coverGrade) : {};
  const inBounds = (c: SquareCell): boolean => c.x >= 0 && c.x < cols && c.y >= 0 && c.y < rows;
  const commands: EncounterCommand[] = [];
  for (const e of runEdges) {
    if (!inBounds(e.a) && !inBounds(e.b)) continue; // fully off-board: no wall to author
    if (sameEdgeFlags(edges[edgeKey(squareSpace, e.a, e.b)], target)) continue; // no-op: already in target state
    commands.push({ type: "set_edge", actorId, a: e.a, b: e.b, edge: target });
  }
  if (commands.length === 0) return null;
  return { type: "batch", commands };
}

// ── Drag-a-box rooms ──────────────────────────────────────────────────────────────
//
// The dedicated **room tool** authors a purely-visual {@link Room} — no mechanical meaning — by
// a single drag-a-box gesture: press a cell, drag, release. The covered cell rectangle is one
// `RoomRect`; the **start cell** decides membership — if it falls inside an existing room the
// rect UNIONS into that room (append to its `rects`), else it CREATES a new room (next `ROOM N`).
//
// **At most one room per cell.** A box may overlap OTHER rooms; the new box wins
// (last-write-wins) and every other room it touches has the contested cells carved out via the
// domain `subtractRoomCells` difference helper — shrinking it, splitting it, or (carved to zero)
// removing it. So `roomBoxCommand` now returns an atomic `batch`: the new/extended room PLUS a
// `set_room`/`remove_room` per carved room, all in ONE undo step.
//
// **On-canvas erase.** An Alt-drag (the same Alt = erase trigger tiles/walls use)
// subtracts its box from whatever room(s) it covers — `roomEraseCommand`, the carve half alone
// (no new room): shrink / carve-hole / auto-delete-on-zero, again ONE atomic `batch`.
//
// Pure pieces drive it (the stage only floors the press/release to cells and dispatches):
//   - `roomRectFromCells` — two corner cells → the clamped `{x,y,w,h}` rect they bound.
//   - `roomForStartCell`  — the existing room whose union contains the drag-start cell, if any.
//   - `roomBoxCommand`    — the finished box as ONE `batch` (new/extended room + carve the rest).
//   - `roomEraseCommand`  — an Alt-drag box as ONE `batch` carving every room it covers.

/** Match for an auto-assigned room name, capturing its number — `ROOM 1`, `ROOM 12`, … */
const ROOM_NAME_RE = /^ROOM (\d+)$/;

/**
 * The next auto-assigned room name — `ROOM N` where `N` is one past the highest number among
 * existing `ROOM <n>` names (so deleting `ROOM 2` of `{1,2,3}` still yields `ROOM 4`, never a
 * collision). A roomset with no numbered names starts at `ROOM 1`. Renamed rooms (names that
 * don't match the pattern) don't perturb the sequence.
 */
export function nextRoomName(rooms: Record<RoomId, Room>): string {
  let max = 0;
  for (const room of Object.values(rooms)) {
    const m = ROOM_NAME_RE.exec(room.name);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `ROOM ${max + 1}`;
}

/**
 * The clamped axis-aligned cell rect two drag-corner cells bound — min-corner `(x,y)` plus
 * `w`×`h`, intersected with the board `[0,cols)×[0,rows)`. Both corners are *inclusive* (a press
 * and release on the same cell is a 1×1 rect). Returns `null` when the clamped box is empty (the
 * whole drag fell off-board) — no off-board room rect is authored (the stage drops it).
 */
export function roomRectFromCells(
  start: SquareCell,
  end: SquareCell,
  board: { cols: number; rows: number },
): RoomRect | null {
  const minX = Math.max(0, Math.min(start.x, end.x));
  const minY = Math.max(0, Math.min(start.y, end.y));
  const maxX = Math.min(board.cols - 1, Math.max(start.x, end.x));
  const maxY = Math.min(board.rows - 1, Math.max(start.y, end.y));
  if (maxX < minX || maxY < minY) return null; // wholly off-board
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/**
 * The existing room whose union contains the drag-**start** cell `(x,y)`, or `undefined` if none
 * — the membership test that picks union-vs-create ({@link roomContainsCell}). When several rooms
 * overlap the start cell (a v1 edge the rule doesn't forbid), the **first** by iteration order is
 * chosen, deterministically.
 */
export function roomForStartCell(rooms: Record<RoomId, Room>, x: number, y: number): Room | undefined {
  return Object.values(rooms).find((room) => roomContainsCell(room, x, y));
}

/** Named arguments for {@link roomBoxCommand}. */
export type RoomBoxArgs = {
  actorId: string;
  /** The drag-start cell (its membership picks union-vs-create). */
  start: SquareCell;
  /** The drag-release cell (with `start`, bounds the new rect). */
  end: SquareCell;
  /** The live rooms map — read for start-cell membership + the next `ROOM N` name. */
  rooms: Record<RoomId, Room>;
  /** A freshly-minted room id for the create-new case (the caller mints it; ignored on union). */
  newId: RoomId;
  /** Board bounds — the rect is clamped to `[0,cols)×[0,rows)`. */
  cols: number;
  rows: number;
  /**
   * **Auto-wall mode**: when `true`, the resulting batch is extended with the
   * `set_edge` children that wall every affected room's new perimeter and clear its interior edges
   * ({@link roomAutoWallCommands}). When falsy the batch is **exactly** today's (room commands only) —
   * the toggle gates which commands the authoring layer composes, it is not itself a command. The live
   * `edges` map (below) is read to drop no-op edge children; pass it whenever `autoWall` is on.
   */
  autoWall?: boolean;
  /** The live edge map — read only when `autoWall` is on, to drop already-correct edge children. */
  edges?: EdgeMap;
};

/** The set of cell keys (`"x,y"`, the {@link subtractRoomCells} convention) a rect covers. */
function rectCellKeys(rect: RoomRect): Set<string> {
  const keys = new Set<string>();
  for (let dy = 0; dy < rect.h; dy++) {
    for (let dx = 0; dx < rect.w; dx++) keys.add(`${rect.x + dx},${rect.y + dy}`);
  }
  return keys;
}

/** Whether any of a room's cells fall in `keys` (the cheap "does this carve touch it?" guard). */
function roomTouchesKeys(room: Room, keys: Set<string>): boolean {
  for (const r of room.rects) {
    for (let dy = 0; dy < r.h; dy++) {
      for (let dx = 0; dx < r.w; dx++) {
        if (keys.has(`${r.x + dx},${r.y + dy}`)) return true;
      }
    }
  }
  return false;
}

/** Named arguments for {@link carveRoomsCommands}. */
type CarveRoomsArgs = {
  rooms: Record<RoomId, Room>;
  /** The cell keys (`"x,y"`) the box claims — subtracted from every room that touches them. */
  keys: Set<string>;
  actorId: string;
  /** The box's host room (if any) — kept whole, never carved (it owns the box). */
  skipId?: RoomId;
};

/**
 * The carve commands that subtract `keys` from every room it touches — the shared core of
 * no-overlap enforcement ({@link roomBoxCommand}) and the on-canvas erase ({@link roomEraseCommand}).
 * For each room (other than `skipId`, the box's host) overlapping `keys`, the surviving cells are
 * re-compressed by {@link subtractRoomCells}: an empty result ⇒ a `remove_room` (carved to zero),
 * else a `set_room` carrying the shrunk/split rects. Rooms the box never touches issue nothing.
 */
function carveRoomsCommands(args: CarveRoomsArgs): EncounterCommand[] {
  const { rooms, keys, actorId, skipId } = args;
  const commands: EncounterCommand[] = [];
  for (const room of Object.values(rooms)) {
    if (room.id === skipId) continue;
    if (!roomTouchesKeys(room, keys)) continue;
    const survivors = subtractRoomCells(room.rects, keys);
    commands.push(
      survivors.length === 0
        ? { type: "remove_room", actorId, roomId: room.id }
        : { type: "set_room", actorId, room: { ...room, rects: survivors } },
    );
  }
  return commands;
}

// ── Auto-wall mode ────────────────────────────────────────────────────────────────
//
// Auto-wall is the ONE place a room's footprint touches the edge layer (rooms otherwise carry no
// mechanical meaning; this adds *edges*, not room mechanics). Given a
// room's resulting footprint (union-of-rects → a cell set), every edge is classified purely from
// cell membership:
//   - **perimeter** edge (an in-footprint cell ↔ a not-in-footprint / off-board cell) → SET a wall
//     (`edgeFlagsFor()`), exactly like the wall tool; a one-off-board side is a legitimate border wall.
//   - **interior** edge (BOTH cells in the footprint — the shared edges of an L/T union, AND an old
//     perimeter a room extension absorbs) → CLEAR to open `{}`, so a wall "moves out" to the new edge.
//   - edges fully outside the footprint (both cells out) → untouched.
// The pure `roomWallEdges(footprint)` decides the geometry; `roomAutoWallCommands` folds it across
// EVERY room a box/erase batch touches (multi-room/carve + remove), resolves set-vs-clear conflicts
// (a removed room's former perimeter a surviving room now walls stays walled — SET wins), drops
// no-op edge children against the live map, and returns the `set_edge` children appended INTO the
// existing room `batch` — ONE batch, one undo reverts room AND walls together.

/** An undirected wall edge as its straddling `(a, b)` cell pair (off-board cells allowed — border walls). */
type EdgeRef = { a: SquareCell; b: SquareCell };

/** The set of cell keys (`"x,y"`) a room's union-of-rects footprint covers. */
function roomFootprint(rects: RoomRect[]): Set<string> {
  const keys = new Set<string>();
  for (const r of rects) {
    for (let dy = 0; dy < r.h; dy++) {
      for (let dx = 0; dx < r.w; dx++) keys.add(`${r.x + dx},${r.y + dy}`);
    }
  }
  return keys;
}

/**
 * The auto-wall edge geometry for a single room footprint (a set of `"x,y"` cell keys): the
 * **perimeter** edges to SET as walls (an in-footprint cell adjacent to a not-in-footprint / off-board
 * cell) and the **interior** edges to CLEAR (both cells in the footprint). Pure geometry — it reads no
 * edge map and writes no command; {@link roomAutoWallCommands} resolves cross-room conflicts and drops
 * no-ops against the live edges. Each edge is canonical-keyed ({@link edgeKey}) so the four-neighbour
 * scan never double-lists a shared interior edge, and a perimeter edge is never also an interior one
 * (interior needs both cells in; perimeter needs one out).
 */
export function roomWallEdges(footprint: Set<string>): { set: EdgeRef[]; clear: EdgeRef[] } {
  const set = new Map<string, EdgeRef>();
  const clear = new Map<string, EdgeRef>();
  for (const key of footprint) {
    const comma = key.indexOf(",");
    const x = Number(key.slice(0, comma));
    const y = Number(key.slice(comma + 1));
    const cell = squareCell(x, y);
    for (const n of [squareCell(x + 1, y), squareCell(x - 1, y), squareCell(x, y + 1), squareCell(x, y - 1)]) {
      const k = edgeKey(squareSpace, cell, n);
      if (footprint.has(`${n.x},${n.y}`)) clear.set(k, { a: cell, b: n });
      else set.set(k, { a: cell, b: n });
    }
  }
  return { set: [...set.values()], clear: [...clear.values()] };
}

/**
 * The `set_edge` children auto-wall appends into a room `batch`, folded across EVERY room the batch
 * touches — the multi-room/carve case. Drives off the batch's own room commands + the pre-gesture
 * `rooms`/`edges`:
 *   - a `set_room` child (the host's new footprint OR a carved survivor's shrunk footprint) → SET its
 *     perimeter, CLEAR its interior ({@link roomWallEdges});
 *   - a `remove_room` child (a room carved to zero) → CLEAR its FORMER perimeter (looked up in `rooms`).
 * **Set wins over clear**: a removed room's former-perimeter edge that a surviving room now walls (the
 * host absorbed its footprint) stays a wall — so the per-room clear is dropped wherever any room sets the
 * same edge. Then every target edge whose live state already matches is dropped (idempotent — a re-wall
 * over an existing wall, a clear of an already-open interior). Order is deterministic (canonical-key
 * sorted; sets before clears). Reads the PRE-gesture `edges` — correct because no room command touches
 * the edge layer, so the live edges are unchanged by the rooms applied earlier in the same batch.
 */
type AutoWallArgs = {
  /** The batch's room commands (carve survivors' `set_room` + the host's `set_room` + any `remove_room`). */
  roomCommands: EncounterCommand[];
  /** The PRE-gesture rooms map — a `remove_room`'s former footprint (for its perimeter clear) is looked up here. */
  rooms: Record<RoomId, Room>;
  /** The PRE-gesture edge map — read to drop already-correct edge children (idempotent set/clear). */
  edges: EdgeMap;
  actorId: string;
};

function roomAutoWallCommands(args: AutoWallArgs): EncounterCommand[] {
  const { roomCommands, rooms, edges, actorId } = args;
  const setMap = new Map<string, EdgeRef>();
  const clearMap = new Map<string, EdgeRef>();
  for (const cmd of roomCommands) {
    if (cmd.type === "set_room") {
      const { set, clear } = roomWallEdges(roomFootprint(cmd.room.rects));
      for (const e of set) setMap.set(edgeKey(squareSpace, e.a, e.b), e);
      for (const e of clear) clearMap.set(edgeKey(squareSpace, e.a, e.b), e);
    } else if (cmd.type === "remove_room") {
      const former = rooms[cmd.roomId];
      if (!former) continue;
      // A removed room's former PERIMETER is cleared (its walls go with it).
      for (const e of roomWallEdges(roomFootprint(former.rects)).set) clearMap.set(edgeKey(squareSpace, e.a, e.b), e);
    }
  }
  for (const k of setMap.keys()) clearMap.delete(k); // SET wins over CLEAR on a contested edge
  const barrier = edgeFlagsFor();
  const commands: EncounterCommand[] = [];
  for (const k of [...setMap.keys()].sort()) {
    const e = setMap.get(k)!;
    if (!sameEdgeFlags(edges[k], barrier)) commands.push({ type: "set_edge", actorId, a: e.a, b: e.b, edge: barrier });
  }
  for (const k of [...clearMap.keys()].sort()) {
    const e = clearMap.get(k)!;
    if (!edgeIsOpen(edges[k])) commands.push({ type: "set_edge", actorId, a: e.a, b: e.b, edge: {} });
  }
  return commands;
}

/**
 * The ONE atomic `batch` a finished drag-a-box gesture commits — or `null` when the box fell
 * wholly off-board (nothing to author). **Start-cell union semantics**: if the drag's start cell
 * is inside an existing room ({@link roomForStartCell}) the new rect is **appended to that room's
 * `rects`** (same id + name); otherwise a **new room** is created (`newId`, the next `ROOM N` name,
 * the single rect). **No-overlap:** the new box wins every contested cell — every OTHER
 * room it overlaps has those cells carved out ({@link carveRoomsCommands} via `subtractRoomCells`),
 * shrinking, splitting, or removing it. The result is a `batch` of the new/extended room's `set_room`
 * plus one `set_room`/`remove_room` per carved room — ONE undo step the reducer inverts atomically
 * (one undo restores every carved room), so no cell is ever in two rooms.
 */
export function roomBoxCommand(args: RoomBoxArgs): EncounterBatchCommand | null {
  const { actorId, start, end, rooms, newId, cols, rows, autoWall, edges } = args;
  const rect = roomRectFromCells(start, end, { cols, rows });
  if (!rect) return null;
  const host = roomForStartCell(rooms, start.x, start.y);
  const room: Room = host
    ? { ...host, rects: [...host.rects, rect] }
    : { id: newId, name: nextRoomName(rooms), rects: [rect] };
  // Every OTHER room yields the box's cells (the new room is the host, which keeps them).
  const carves = carveRoomsCommands({ rooms, keys: rectCellKeys(rect), actorId, skipId: host?.id });
  const roomCommands: EncounterCommand[] = [...carves, { type: "set_room", actorId, room }];
  // Auto-wall: fold the perimeter/interior edge children into the SAME batch (one undo).
  const wallCommands = autoWall ? roomAutoWallCommands({ roomCommands, rooms, edges: edges ?? {}, actorId }) : [];
  return { type: "batch", commands: [...roomCommands, ...wallCommands] };
}

/** Named arguments for {@link roomEraseCommand}. */
export type RoomEraseArgs = {
  actorId: string;
  /** The drag-start cell (with `end`, bounds the erase box). */
  start: SquareCell;
  /** The drag-release cell. */
  end: SquareCell;
  /** The live rooms map — every room the box covers is carved. */
  rooms: Record<RoomId, Room>;
  /** Board bounds — the box is clamped to `[0,cols)×[0,rows)`. */
  cols: number;
  rows: number;
  /**
   * **Auto-wall mode**: when `true`, the batch is extended with the `set_edge`
   * children that re-derive every shrunk room's perimeter and clear a removed room's former perimeter
   * ({@link roomAutoWallCommands}); when falsy the batch is exactly today's (carve commands only).
   */
  autoWall?: boolean;
  /** The live edge map — read only when `autoWall` is on, to drop already-correct edge children. */
  edges?: EdgeMap;
};

/**
 * The ONE atomic `batch` an Alt-drag room **erase** commits — or `null` when nothing is affected
 * (the box fell off-board, or covered no room). The same Alt = erase trigger tiles/walls use
 * (terrain-editing-overhaul task 1.4): the box's cells are subtracted from every room it covers
 * ({@link carveRoomsCommands} — no host skipped, no new room created), so each hit room shrinks,
 * carves a hole, or (covered entirely) is removed via the `remove_room`-on-last-rect path. ONE
 * batch ⇒ ONE undo step that restores every affected room together.
 */
export function roomEraseCommand(args: RoomEraseArgs): EncounterBatchCommand | null {
  const { actorId, start, end, rooms, cols, rows, autoWall, edges } = args;
  const rect = roomRectFromCells(start, end, { cols, rows });
  if (!rect) return null;
  const roomCommands = carveRoomsCommands({ rooms, keys: rectCellKeys(rect), actorId });
  if (roomCommands.length === 0) return null; // erased over open floor — nothing to author
  // Auto-wall: re-derive every shrunk room's perimeter + clear a removed room's former walls.
  const wallCommands = autoWall ? roomAutoWallCommands({ roomCommands, rooms, edges: edges ?? {}, actorId }) : [];
  return { type: "batch", commands: [...roomCommands, ...wallCommands] };
}

// ── Inline name editing ───────────────────────────────────────────────────────────
//
// The shared inspector name field (`NameField`, `lib/encounter/CombatWindows.tsx`) keeps a local
// `draft` that diverges from the committed `name` while the GM types, committing on blur/Enter.
// Its whole decision surface is two pure functions, tested without a DOM (the frontend test runner
// has no jsdom): when an external `name` lands, whether to overwrite the draft; and how a blurred
// edit resolves (commit vs snap-back). Keeping the logic here (not inline in the component) is the
// same "pure, testable heart" call the rest of this file makes for the terrain gestures.

/**
 * The draft a name field should show when a new external `name` arrives. **While the field is being
 * edited (focused), the in-progress `draft` is kept** — a sync broadcast (another actor's rename),
 * an undo, or an optimistic/confirm race landing a different `name` must NOT clobber the GM's
 * keystrokes. When the field is idle the external `incoming` value is adopted (so an external rename
 * or an undo is reflected). This is the fix for the old `useEffect(() => setDraft(name), [name])`,
 * which overwrote the draft unconditionally.
 */
export function nextNameDraft(incoming: string, editing: boolean, draft: string): string {
  return editing ? draft : incoming;
}

/**
 * How a blurred/Enter-committed name edit resolves, given the field `draft` and the current
 * committed `name`. The draft is trimmed; an **empty** or **unchanged** result snaps back to `name`
 * and issues no command (`{ snapBack }`), else the trimmed value commits (`{ commit }`). Pure, so the
 * field's commit decision is the SAME whether driven by a blur, an Enter key, or a test.
 */
export function resolveNameCommit(draft: string, name: string): { commit: string } | { snapBack: string } {
  const n = draft.trim();
  if (n === "" || n === name) return { snapBack: name };
  return { commit: n };
}
